import React, { useState, useEffect, useMemo } from "react";
import CustomButton from "components/CustomButton";
import Loader from "components/Loader";
import { useToasts } from "react-toast-notifications";
import UserService from "services/UserService";
import Scrollbar from "react-scrollbars-custom";
import UserStrengthAlertModal from "./UserStrengthAlertModal";

const styles = {
  basicInfoContainer: {
    marginTop: "20px",
  },
  modalActions: {
    justifyContent: "center",
    marginTop: "10px",
  },
  tabContainer: {
    display: "flex",
    justifyContent: "center",
    gap: "10px",
    marginBottom: "20px",
    padding: "0 20px",
  },
  tabButton: {
    flex: 1,
    maxWidth: "200px",
    padding: "10px 20px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    backgroundColor: "#fff",
    cursor: "pointer",
    transition: "all 0.3s ease",
    fontSize: "14px",
    fontWeight: "500",
  },
  regularActiveTab: {
    backgroundColor: "#69077f",
    color: "#fff",
    borderColor: "#69077f",
  },
  mentorMachineActiveTab: {
    backgroundColor: "#0b78af",
    color: "#fff",
    borderColor: "#0b78af",
  },
  mentorMachineWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    width: "auto",
    maxWidth: "100%",
    margin: "0 auto",
    padding: "0 10px",
  },
  quadrantContainer: {
    display: "flex",
    flexDirection: "column",
    marginBottom: "30px",
    width: "100%",
  },
  quadrantHeader: {
    fontSize: "16px",
    fontWeight: "600",
    marginBottom: "15px",
    color: "#333",
    textAlign: "left",
    width: "100%",
  },
  quadrantStrengths: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    width: "100%",
    justifyContent: "flex-start",
  },
};
// Quadrant mapping for Mentor Machine Strength Index
const QUADRANT_MAPPING = {
  5: "Pain",
  6: "Perception",
  7: "People",
  8: "Purpose",
};

export default ({
  loading,
  strengths,
  onCloseModal,
  setSelectedStrengths,
  userStrength,
}) => {
  const classes = styles;
  const { addToast } = useToasts();
  const [selectedStrength, setSelectedStrength] = useState([]);
  const [selectedStrengths, setSelectedAllStrength] = useState([]);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [showStrengthAlertModal, setStrengthAlertModal] = useState(false);
  const [activeTab, setActiveTab] = useState("regular"); // 'regular' or 'mentorMachine'
  const [lockedCategory, setLockedCategory] = useState(null); // null, 'regular', or 'mentorMachine'

  // Separate strengths into Regular (1-4) and Mentor Machine (5-8)
  const { regularStrengths, mentorMachineStrengths } = useMemo(() => {
    const regular = strengths?.filter((s) => s.strength_group_id >= 1 && s.strength_group_id <= 4) || [];
    const mentorMachine = strengths?.filter((s) => s.strength_group_id >= 5 && s.strength_group_id <= 8) || [];
    return { regularStrengths: regular, mentorMachineStrengths: mentorMachine };
  }, [strengths]);

  // Group Mentor Machine strengths by quadrant
  const groupedMentorMachineStrengths = useMemo(() => {
    const grouped = {};
    mentorMachineStrengths.forEach((strength) => {
      const groupId = strength.strength_group_id;
      if (!grouped[groupId]) {
        grouped[groupId] = [];
      }
      grouped[groupId].push(strength);
    });
    return Object.keys(grouped)
      .sort((a, b) => Number(a) - Number(b))
      .map((groupId) => ({
        groupId: Number(groupId),
        quadrantName: QUADRANT_MAPPING[Number(groupId)],
        strengths: grouped[groupId],
      }));
  }, [mentorMachineStrengths]);

  // Determine initial category based on user's existing strengths
  useEffect(() => {
    if (userStrength && userStrength?.length > 0) {
      setSelectedAllStrength(userStrength);
      setSelectedStrength(userStrength?.map((item) => item.id));
      
      const hasMentorMachineStrengths = userStrength.some(
        (s) => s.strength_group_id >= 5 && s.strength_group_id <= 8
      );
      const hasRegularStrengths = userStrength.some(
        (s) => s.strength_group_id >= 1 && s.strength_group_id <= 4
      );
      
      if (hasMentorMachineStrengths) {
        setLockedCategory("mentorMachine");
        setActiveTab("mentorMachine");
      } else if (hasRegularStrengths) {
        setLockedCategory("regular");
        setActiveTab("regular");
      }
    }
  }, [userStrength]);

  const displayedStrengths = activeTab === "regular" ? regularStrengths : mentorMachineStrengths;

  const handleTabChange = (tab) => {
    if (lockedCategory === null || lockedCategory === tab) {
      setActiveTab(tab);
    }
  };

  const handleStrength = (strength) => {
    const isRegular = strength.strength_group_id >= 1 && strength.strength_group_id <= 4;
    const isMentorMachine = strength.strength_group_id >= 5 && strength.strength_group_id <= 8;
    const strengthCategory = isRegular ? "regular" : isMentorMachine ? "mentorMachine" : null;

    if (lockedCategory === null && strengthCategory) {
      setLockedCategory(strengthCategory);
      if (activeTab !== strengthCategory) {
        setActiveTab(strengthCategory);
      }
    }

    if (lockedCategory !== null && lockedCategory !== strengthCategory) {
      return;
    }

    if (
      !selectedStrength.includes(strength.id) &&
      selectedStrength.length < 3
    ) {
      setSelectedStrength([...selectedStrength, strength.id]);
      setSelectedAllStrength([...selectedStrengths, strength]);
      return;
    }
    if (selectedStrength.includes(strength.id)) {
      const newSelectedStrength = selectedStrength.filter((item) => item !== strength.id);
      const newSelectedStrengths = selectedStrengths.filter((item) => item.id !== strength.id);
      
      setSelectedStrength(newSelectedStrength);
      setSelectedAllStrength(newSelectedStrengths);
      
      if (newSelectedStrength.length === 0) {
        setLockedCategory(null);
      }
      return;
    }
    setStrengthAlertModal(true);
  };

  const handleOnClick = async () => {
    setUpdateLoading(true);
    try {
      const response = await UserService.updateUserStrengthList(
        selectedStrength
      );
      setUpdateLoading(false);
      setSelectedStrengths(selectedStrengths);
      onCloseModal();
      setSelectedStrength([]);
      addToast(
        response?.data?.message || "User Strengths updated successfully",
        {
          appearance: "success",
        }
      );
    } catch (e) {
      setUpdateLoading(false);
      addToast(e?.response?.data?.message || "Url Not Found", {
        appearance: "error",
      });
      console.log(e);
    }
  };

  const getStrengthGroupClass = (strength) => {
    const groupId = strength.strength_group_id;
    const isSelected = selectedStrength.includes(strength.id);
    
    // Regular strengths (1-4)
    if (groupId === 1) {
      return isSelected ? "strengthGroupOneOnClick" : "strengthGroupOne";
    } else if (groupId === 2) {
      return isSelected ? "strengthGroupTwoOnClick" : "strengthGroupTwo";
    } else if (groupId === 3) {
      return isSelected ? "strengthGroupThreeOnClick" : "strengthGroupThree";
    } else if (groupId === 4) {
      return isSelected ? "strengthGroupFourOnClick" : "strengthGroupFour";
    }
    // Mentor Machine Strength Index (5-8)
    else if (groupId === 5) {
      return isSelected ? "strengthGroupFiveOnClick" : "strengthGroupFive";
    } else if (groupId === 6) {
      return isSelected ? "strengthGroupSixOnClick" : "strengthGroupSix";
    } else if (groupId === 7) {
      return isSelected ? "strengthGroupSevenOnClick" : "strengthGroupSeven";
    } else if (groupId === 8) {
      return isSelected ? "strengthGroupEightOnClick" : "strengthGroupEight";
    }
    return "";
  };

  const isTabDisabled = (tab) => {
    return lockedCategory !== null && lockedCategory !== tab;
  };

  return (
    <>
      {showStrengthAlertModal && (
        <UserStrengthAlertModal
          strengthModal={showStrengthAlertModal}
          setStrengthModal={setStrengthAlertModal}
        />
      )}
      <div className="modalContent">
        {/* Tab buttons */}
        <div className="strengthTabContainer" style={classes.tabContainer}>
          <button
            className="tabButtonRegular"
            style={{
              ...classes.tabButton,
              ...(activeTab === "regular" ? classes.regularActiveTab : {}),
              ...(isTabDisabled("regular") ? { opacity: 0.5, cursor: "not-allowed" } : {}),
            }}
            onClick={() => handleTabChange("regular")}
            disabled={isTabDisabled("regular")}
          >
            Regular Strengths
          </button>
          <button
            className="tabButtonMentorMachine"
            style={{
              ...classes.tabButton,
              ...(activeTab === "mentorMachine" ? classes.mentorMachineActiveTab : {}),
              ...(isTabDisabled("mentorMachine") ? { opacity: 0.5, cursor: "not-allowed" } : {}),
            }}
            onClick={() => handleTabChange("mentorMachine")}
            disabled={isTabDisabled("mentorMachine")}
          >
            Mentor Machine Strength Index
          </button>
        </div>
        <Scrollbar className="strengthModalScrollbar" style={{ height: `calc(100vh - 300px)` }}>
          <div className="strengthModalContent">
            {loading ? (
              <Loader width={40} height={40} />
            ) : (
              <>
                {activeTab === "regular" ? (
                  displayedStrengths?.map((strength) => {
                    const isRegular = strength.strength_group_id >= 1 && strength.strength_group_id <= 4;
                    const strengthCategory = isRegular ? "regular" : null;
                    const isDisabled = lockedCategory !== null && lockedCategory !== strengthCategory;
                    
                    return (
                      <React.Fragment key={strength.id}>
                        <CustomButton
                          buttonClass={`strength ${getStrengthGroupClass(strength)}`}
                          onClick={() => handleStrength(strength)}
                          disabled={isDisabled}
                          buttonStyle={{
                            opacity: isDisabled ? 0.5 : 1,
                            cursor: isDisabled ? "not-allowed" : "pointer",
                          }}
                        >
                          {strength.name}
                        </CustomButton>
                      </React.Fragment>
                    );
                  })
                ) : (
                  <div className="mentorMachineWrapper" style={classes.mentorMachineWrapper}>
                    {groupedMentorMachineStrengths.map((quadrant) => (
                      <div key={quadrant.groupId} className="quadrantContainer" style={classes.quadrantContainer}>
                        <div className="quadrantHeader" style={classes.quadrantHeader}>
                          {quadrant.quadrantName}
                        </div>
                        <div className="quadrantStrengths" style={classes.quadrantStrengths}>
                          {quadrant.strengths.map((strength) => {
                            const isMentorMachine = strength.strength_group_id >= 5 && strength.strength_group_id <= 8;
                            const strengthCategory = isMentorMachine ? "mentorMachine" : null;
                            const isDisabled = lockedCategory !== null && lockedCategory !== strengthCategory;
                            
                            return (
                              <CustomButton
                                key={strength.id}
                                buttonClass={`strength ${getStrengthGroupClass(strength)}`}
                                onClick={() => handleStrength(strength)}
                                disabled={isDisabled}
                                buttonStyle={{
                                  opacity: isDisabled ? 0.5 : 1,
                                  cursor: isDisabled ? "not-allowed" : "pointer",
                                }}
                              >
                                {strength.name}
                              </CustomButton>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </Scrollbar>
      </div>
      <div className="modalAction" style={classes.modalActions}>
        <CustomButton
          buttonClass="btn grad_btn"
          buttonStyle={{ width: "200px" }}
          onClick={handleOnClick}
          disabled={selectedStrength?.length === 0}
        >
          {!updateLoading ? "Submit" : <Loader width={40} height={40} />}
        </CustomButton>
      </div>
    </>
  );
};
